'use client';

/**
 * 2026-07-30 老板需求：新建"习题演练"独立入口页（模仿 dongniai/ 主页布局）
 * - 路径：/exercise/（basePath=/dongniai → 实际访问 /dongniai/exercise/）
 * - 功能：填主题提示词 + 选演练类型 + 填基本情况 → 弹二次确认 → 跳独立做题页 /exercise/[sessionId]
 * - 与 /exercise/[sessionId]/page.tsx 共存：静态路由 page.tsx 优先于动态路由 [sessionId]/page.tsx
 * - 题目生成 / AI 批改 / 扣 2 分钟课时逻辑全部复用已有的 /api/exercise/start（不动后端）
 * - 不引入 LLM / classroom 流程：保持纯"习题演练"特色（与 dongniai/ 主页的"进入课堂"流程解耦）
 * - TopNav 由本页面自己渲染（与 dongniai/ 主页一致；layout 不自动挂 TopNav）
 */

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { motion } from 'motion/react';
import {
  ClipboardCheck,
  Sparkles,
  ArrowLeft,
  FileText,
  BookOpen,
  GraduationCap,
  Trophy,
  Target,
  Brain,
  Library,
  Loader2,
  Award,
  Crown,
  MoreHorizontal,
} from 'lucide-react';
import { TopNav } from '@/components/top-nav';
import { useAuthStore } from '@/lib/store/auth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

const log = createLogger('Exercise Home');

// 2026-07-30 习题演练入口页：force-dynamic 防止 prerender 命中 404 缓存
export const dynamic = 'force-dynamic';

const EXERCISE_TYPES = [
  { value: '同步教材练习', icon: BookOpen, desc: '覆盖当前章节核心知识点' },
  { value: '期中测试卷', icon: Trophy, desc: '适合期中考试综合测试' },
  { value: '期末测试卷', icon: GraduationCap, desc: '适合期末考试综合测试' },
  { value: '错题强化训练', icon: Target, desc: '针对错题薄弱点强化' },
  { value: '收藏题强化训练', icon: Library, desc: '针对收藏题强化训练' },
  { value: '知识点专项练习', icon: Brain, desc: '围绕核心知识点专项练习' },
  // 2026-07-30 老板需求：加 中考模拟 / 高考模拟 / 其他（其他需要自定义文本）
  { value: '中考模拟', icon: Award, desc: '按中考真题难度与考点范围' },
  { value: '高考模拟', icon: Crown, desc: '按高考真题难度与考点范围' },
  { value: '其他', icon: MoreHorizontal, desc: '点击后在下方填写自定义演练类型', custom: true },
] as const;

// 2026-07-30 v2 老板需求：阶段跟 dongniai/plan/myForms 完全一致（addons/dongniai/view/plan/myForms.html）
//   - myForms 阶段列表：学前 / 小学 / 初中 / 高中 / 专科 / 本科 / 硕士 / 博士 / 博士后 / 成人 / 其他（共 11 项）
//   - GRADE_MAP：小学-博士 7 个有联动年级选项；学前/成人/其他 显示年龄输入；博士后 跳过（直接进入下一步）
const EDUCATION_LEVELS = ['学前', '小学', '初中', '高中', '专科', '本科', '硕士', '博士', '博士后', '成人', '其他'];
const GRADE_MAP: Record<string, string[]> = {
  '小学': ['一年级', '二年级', '三年级', '四年级', '五年级', '六年级'],
  '初中': ['初一', '初二', '初三'],
  '高中': ['高一', '高二', '高三'],
  '专科': ['大一', '大二', '大三'],
  '本科': ['大一', '大二', '大三', '大四'],
  '硕士': ['研一', '研二', '研三'],
  '博士': ['博一', '博二', '博三', '博四', '博五'],
};

export default function ExerciseHomePage() {
  const router = useRouter();
  const { loggedIn, checked } = useAuthStore();

  // 表单 state
  const [requirement, setRequirement] = useState('');
  const [exerciseType, setExerciseType] = useState<string>('同步教材练习');
  // 2026-07-30 老板需求：选"其他"时需要填具体演练类型（如"高考冲刺"、"竞赛模拟"等）
  const [exerciseTypeCustom, setExerciseTypeCustom] = useState('');
  const [educationLevel, setEducationLevel] = useState('');
  const [educationGrade, setEducationGrade] = useState('');
  // 2026-07-30 v5 老板需求：点击"年级/年龄"输入框时直接弹出所有联动选项（datalist 默认行为需要先输入文字才显示）
  const [gradeDropdownOpen, setGradeDropdownOpen] = useState(false);
  const gradeInputRef = useRef<HTMLInputElement | null>(null);
  const [subject, setSubject] = useState('');
  const [textbookVersion, setTextbookVersion] = useState('');

  // 二次确认弹窗 state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 2026-08-02 老板 P0：mobile 端「我的题库」悬浮按钮 (FAB) 开关
  //  - PC 端底部 4 按钮直接展示；mobile 端单 FAB，点击展开 4 项 bottom sheet
  const [quizSheetOpen, setQuizSheetOpen] = useState(false);

  // 2026-08-02 v6.97.58 — 习题抵扣课时配置（从 /dongniai_public.php?method=exercise_bill_config 拉取）
  //   - 默认 2 分钟（DB 没配置时）
  //   - admin 后台改「习题抵扣课时配置」后，前端 ≤1 秒内自动同步（每次进入页面 fetch 一次）
  const [exerciseMinutes, setExerciseMinutes] = useState<number>(2);

  // 登录态未确认前显示骨架，避免闪烁
  useEffect(() => {
    useAuthStore.getState().fetchStatus();
  }, []);

  // 2026-08-02 v6.97.58 — 拉取「习题抵扣课时配置」（无登录要求，公开端点）
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/dongniai_public.php?method=exercise_bill_config', { cache: 'no-store' });
        if (!res.ok) return;
        const json = (await res.json()) as { code: number; data?: { exercise_minutes: number; similar_minutes: number } };
        if (!cancelled && json.code === 1 && json.data && typeof json.data.exercise_minutes === 'number') {
          setExerciseMinutes(json.data.exercise_minutes);
        }
      } catch {
        // 失败时保留默认值 2，行为不变
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // localStorage 持久化"基本情况"，下次访问自动加载（与 dongniai/ 主页 openExerciseConfig 一致）
  useEffect(() => {
    try {
      const cached = localStorage.getItem('exercise_home_last_config');
      if (cached) {
        const p = JSON.parse(cached);
        if (p.educationLevel) setEducationLevel(p.educationLevel);
        if (p.educationGrade) setEducationGrade(p.educationGrade);
        if (p.subject) setSubject(p.subject);
        if (p.textbookVersion) setTextbookVersion(p.textbookVersion);
        if (p.exerciseType) setExerciseType(p.exerciseType);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // 实时持久化基本情况变化
  useEffect(() => {
    try {
      localStorage.setItem(
        'exercise_home_last_config',
        JSON.stringify({ educationLevel, educationGrade, subject, textbookVersion, exerciseType }),
      );
    } catch {
      /* ignore */
    }
  }, [educationLevel, educationGrade, subject, textbookVersion, exerciseType]);

  // 校验是否可以开始
  // 2026-07-30 老板需求：年级/年龄也改成必填项（4 字段都必填：阶段/年级/科目/教材版本可选）
  // 2026-07-30 v3 老板需求：选"其他"时必须填具体自定义演练类型
  const canStart =
    !!subject &&
    !!educationLevel &&
    !!educationGrade &&
    !!exerciseType &&
    (exerciseType !== '其他' || exerciseTypeCustom.trim().length > 0) &&
    !submitting;

  const handleStartClick = () => {
    // 【v6.97.67 P0 修复 2026-08-05 老板报】之前 if (!checked) return 是 silent no-op,
    //   老板点开始演练按钮没反应也不知道为什么 (fetchStatus 异步还没完成).
    // 修法: 检查中显示 loading 状态, 加 toast 提示等待
    if (!checked) {
      toast.info('登录状态确认中，请稍候再试…');
      // 强制重触发 fetchStatus 防止卡死
      void useAuthStore.getState().fetchStatus();
      return;
    }
    if (!loggedIn) {
      toast.error('请先登录');
      // 【v6.97.67 P0 修复 2026-08-05 老板报】点开始演练 → router.push 跳 /index/user/login.html
      //   basePath=/dongniai/ 把绝对路径 /index/user/login.html 当作相对路径, 实际跳到
      //   /dongniai/index/user/login.html → nginx 没这个 location → 兜底 @fastadmin →
      //   PHP KoOIsYnLPb.php 没这个 route → 404 "页面没找到"
      // 修法: 用 window.location.href + 完整域名绝对路径, 绕开 basePath 重写 + 直接走 PHP 登录页
      const target = '/index/user/login.html?url=' + encodeURIComponent('/dongniai/exercise/');
      window.location.href = window.location.origin + target;
      return;
    }
    if (!canStart) {
      toast.error('请填写必填项：阶段 / 年级/年龄 / 科目');
      return;
    }
    setConfirmOpen(true);
  };

  // 确认开启：写 sessionStorage + 跳独立做题页 /exercise/[sessionId]
  const handleConfirmStart = () => {
    if (!canStart) return;
    setSubmitting(true);
    const sessionId = 'ex' + Math.random().toString(36).substring(2, 12);
    // 2026-07-30 v3 老板需求：选"其他"时 lessonTitle / 传给后端的 exerciseType 都用具体自定义名
    const finalExerciseType = exerciseType === '其他' ? exerciseTypeCustom.trim() : exerciseType;
    const cfgPayload = {
      requirement: requirement.trim() || buildExercisePrompt(),
      exerciseType: finalExerciseType,
      subject,
      educationLevel,
      educationGrade,
      textbookVersion,
      roomId: sessionId,
      lessonTitle: `习题演练-${subject}-${educationLevel || ''}-${finalExerciseType}`,
    };
    try {
      sessionStorage.setItem(`exercise_cfg_${sessionId}`, JSON.stringify(cfgPayload));
    } catch (e) {
      log.error('write sessionStorage failed:', e);
    }
    // 【2026-07-30 P0 修复】必须显式带 basePath /dongniai/，否则 nginx 会把 /exercise/xxx/ 路由到 PHP-FPM (ThinkPHP) → 跳会员中心
    //  与 TopNav 上"立即上课"按钮的 href 必须显式 /dongniai/ 同源问题
    window.location.href = `/dongniai/exercise/${sessionId}/`;
  };

  // 自动拼 prompt（无主题时 fallback 用配置生成）
  const buildExercisePrompt = (): string => {
    const parts: string[] = [];
    // 2026-07-30 v3 老板需求：选"其他"时 prompt 也用具体自定义名
    const finalExerciseType = exerciseType === '其他' ? exerciseTypeCustom.trim() : exerciseType;
    parts.push(`【演练类型】${finalExerciseType}`);
    if (educationLevel) parts.push(`【阶段】${educationLevel}`);
    if (educationGrade) parts.push(`【年级】${educationGrade}`);
    if (subject) parts.push(`【科目】${subject}`);
    if (textbookVersion) parts.push(`【教材版本】${textbookVersion}`);
    const typeReq: Record<string, string> = {
      '同步教材练习': '生成一套同步教材练习题，覆盖当前章节核心知识点',
      '期中测试卷': '生成一套适合期中考试的综合测试题',
      '期末测试卷': '生成一套适合期末考试的综合测试题',
      '错题强化训练': '针对错题薄弱知识点生成错题强化训练',
      '收藏题强化训练': '针对收藏题知识点生成对应题型强化训练',
      '知识点专项练习': '围绕核心知识点生成专项练习题',
      // 2026-07-30 老板需求：新增 中考模拟 / 高考模拟
      '中考模拟': '按中考真题难度与考点范围，生成一套初中毕业升学考试模拟卷',
      '高考模拟': '按高考真题难度与考点范围，生成一套普通高等学校招生全国统一考试模拟卷',
    };
    const req = typeReq[exerciseType];
    if (req) parts.push(`【要求】${req}`);
    return parts.join('\n');
  };

  return (
    <>
      <TopNav />

      {/* Background Decor — 与 dongniai/ 主页一致的渐变光晕 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: '5s' }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: '7s' }}
        />
      </div>

      <div className="relative z-10 min-h-[100dvh] w-full bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 flex flex-col items-center p-4 pt-16 md:p-8 md:pt-16 overflow-x-hidden">
        {/* ── Hero 区：图标 + 标题 + 副标题 ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="relative z-20 w-full max-w-[800px] flex flex-col items-center mt-[6vh]"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.05, type: 'spring', stiffness: 200, damping: 20 }}
            className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 shadow-lg shadow-emerald-500/30"
          >
            <ClipboardCheck className="h-9 w-9 text-white" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="text-2xl md:text-3xl font-black tracking-tight text-slate-900 dark:text-slate-100"
          >
            AI 习题演练
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="text-sm text-muted-foreground/70 mt-2 text-center"
          >
            专属试卷生成 · AI 智能批改 · 错题强化训练
          </motion.p>
        </motion.div>

        {/* ── 输入卡片 ── */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.35 }}
          className="relative z-20 w-full max-w-[800px] mt-8"
        >
          <div className="w-full rounded-2xl border border-border/60 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl shadow-xl shadow-black/[0.03] dark:shadow-black/20 p-6 md:p-7">
            {/* 主题提示词 textarea */}
            <div className="mb-5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5 flex items-center gap-1.5">
                <FileText className="size-3.5 text-emerald-600" />
                <span>演练主题（可选）</span>
                <span className="text-slate-400 text-[11px] font-normal">· 不填则按下方配置自动生成</span>
              </label>
              <textarea
                value={requirement}
                onChange={(e) => setRequirement(e.target.value)}
                placeholder="例如：人教版六年级数学上册《分数除法》单元练习 / 期中测试卷 / 错题强化..."
                rows={3}
                className="w-full resize-none border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 px-3 py-2 text-[13px] leading-relaxed placeholder:text-muted-foreground/40 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
              />
            </div>

            {/* 演练类型 6 卡片 */}
            <div className="mb-5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-1.5">
                <Sparkles className="size-3.5 text-emerald-600" />
                <span>演练类型</span>
                <span className="text-rose-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {EXERCISE_TYPES.map((t) => {
                  const Icon = t.icon;
                  const selected = exerciseType === t.value;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setExerciseType(t.value)}
                      className={cn(
                        'flex flex-col items-start gap-1 px-3 py-2.5 rounded-lg border text-left transition-all',
                        selected
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 ring-2 ring-emerald-200 dark:ring-emerald-800 shadow-sm'
                          : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300 hover:bg-emerald-50/40 dark:hover:bg-emerald-900/10 bg-white dark:bg-slate-800',
                      )}
                    >
                      <div className="flex items-center gap-1.5">
                        <Icon className={cn('size-3.5', selected ? 'text-emerald-600' : 'text-slate-500')} />
                        <span className={cn('text-xs font-medium', selected ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-300')}>{t.value}</span>
                      </div>
                      <span className="text-[10px] text-slate-500 dark:text-slate-400 leading-snug">{t.desc}</span>
                    </button>
                  );
                })}
              </div>
              {/* 2026-07-30 老板需求：选"其他"时弹自定义文本输入框 */}
              {exerciseType === '其他' && (
                <div className="mt-3">
                  <label className="text-[11px] text-slate-500 mb-1 flex items-center gap-1">
                    <span>请输入自定义演练类型</span>
                    <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={exerciseTypeCustom}
                    onChange={(e) => setExerciseTypeCustom(e.target.value)}
                    placeholder="如：高考冲刺 / 竞赛模拟 / 英语口语专项 / 数学奥赛..."
                    maxLength={50}
                    className="w-full h-9 px-3 text-xs border border-emerald-300 dark:border-emerald-700 rounded-md bg-white dark:bg-slate-800 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                    autoFocus
                  />
                </div>
              )}
            </div>

            {/* 基本情况 4 字段 — 2026-07-30 老板需求：年级联动选项（跟 myForms 一致）、科目/教材版本改填写 */}
            <div className="mb-5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-1.5">
                <GraduationCap className="size-3.5 text-emerald-600" />
                <span>学习基本情况</span>
                <span className="text-rose-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                <div>
                  <label className="text-[11px] text-slate-500">阶段 *</label>
                  <select
                    className="mt-1 w-full h-9 px-2 text-xs border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800"
                    value={educationLevel}
                    onChange={(e) => {
                      const lv = e.target.value;
                      setEducationLevel(lv);
                      // 阶段切换 → 清掉旧年级（避免跨阶段不匹配）
                      if (educationGrade && GRADE_MAP[lv] && !GRADE_MAP[lv].includes(educationGrade)) {
                        setEducationGrade('');
                      }
                    }}
                  >
                    <option value="">— 阶段 —</option>
                    {EDUCATION_LEVELS.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-slate-500">年级/年龄 <span className="text-rose-500">*</span></label>
                  {/* 2026-07-30 v2 老板需求：跟 myForms 一样
                       - 有 GRADE_MAP 的阶段（小学-博士 7 个）→ 联动下拉 + 允许自由填
                       - 无 GRADE_MAP 的阶段（学前/博士后/成人/其他 4 个）→ 仅 placeholder 提示（如"3 岁"），必填
                     2026-07-30 v5 老板需求：点击输入框即弹所有联动选项（datalist 默认需要先输入才显示）
                       - onFocus 打开自定义下拉浮层（点空白处关闭）
                       - onMouseDown preventDefault 让 dropdown 项点击先于 blur 触发
                     2026-07-31 P0 修复：去掉 datalist
                       - 之前同时存在 datalist（原生浏览器下拉）+ 自定义 dropdown → 老板实测"2 排下拉框"重叠
                       - 保留自定义 dropdown + 加 filter 筛选（边输边筛体验不变）
                       - 无 GRADE_MAP 的阶段不弹下拉（只有 placeholder） */}
                  <div className="relative">
                    <div className="relative">
                      <input
                        ref={gradeInputRef}
                        type="text"
                        autoComplete="off"
                        placeholder={
                          GRADE_MAP[educationLevel]?.length
                            ? '点击选择年级 / 也可自由填写'
                            : educationLevel === '学前'
                            ? '如：3岁 / 学前班'
                            : educationLevel === '博士后'
                            ? '如：研究方向 / 自由填写'
                            : '如：30岁 / 自由填写'
                        }
                        className={cn(
                          'mt-1 w-full h-9 pl-2 pr-8 text-xs border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800',
                          GRADE_MAP[educationLevel]?.length && 'cursor-pointer'
                        )}
                        value={educationGrade}
                        onChange={(e) => setEducationGrade(e.target.value)}
                        onFocus={() => {
                          // 2026-07-30 v6：onFocus 打开（之前 v5 改了但老板实测"多次点击没出"，可能是 React 18 + datalist input 焦点事件竞态）
                          if (GRADE_MAP[educationLevel]?.length) setGradeDropdownOpen(true);
                        }}
                        onClick={() => {
                          // v6 新增：点击也打开（确保即使 onFocus 抢跑也能弹）
                          if (GRADE_MAP[educationLevel]?.length) setGradeDropdownOpen(true);
                        }}
                        onBlur={() => {
                          // setTimeout 让 mousedown 先触发 dropdown 项选择
                          setTimeout(() => setGradeDropdownOpen(false), 200);
                        }}
                        maxLength={20}
                      />
                      {/* 显式 ▼ 按钮（始终在右侧，提示用户可点开） */}
                      {GRADE_MAP[educationLevel]?.length > 0 && (
                        <button
                          type="button"
                          tabIndex={-1}
                          aria-label="展开年级选项"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setGradeDropdownOpen((v) => !v)}
                          className="absolute right-1 top-1/2 -translate-y-1/2 mt-[2px] h-7 w-7 flex items-center justify-center text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded"
                        >
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                    </div>
                    {/* 自定义下拉浮层 — 点击输入框/▼ 即弹所有联动选项 + 边输边筛
                       2026-07-31 P0：去掉 datalist 后只此一处下拉，避免"2 排下拉框"重叠 */}
                    {gradeDropdownOpen && GRADE_MAP[educationLevel]?.length > 0 && (() => {
                      const allGrades = GRADE_MAP[educationLevel] || [];
                      // 2026-07-31：边输边筛 — 输入文字时只显示包含关键词的项（保留自由填能力：空输入显示全部）
                      const keyword = educationGrade.trim();
                      const filtered = keyword
                        ? allGrades.filter((g) => g.includes(keyword))
                        : allGrades;
                      return (
                        <div
                          className="absolute z-30 top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg max-h-60 overflow-y-auto"
                          onMouseDown={(e) => {
                            // 防止 input blur 抢先触发导致 dropdown 项点击失效
                            e.preventDefault();
                          }}
                        >
                          {filtered.length > 0 ? (
                            filtered.map((v) => (
                              <button
                                key={v}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setEducationGrade(v);
                                  setGradeDropdownOpen(false);
                                  gradeInputRef.current?.focus();
                                }}
                                className={cn(
                                  'w-full text-left px-3 py-2 text-xs transition-colors',
                                  'hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-300',
                                  educationGrade === v && 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:hover:text-indigo-300 font-medium'
                                )}
                              >
                                {v}
                              </button>
                            ))
                          ) : (
                            // 无匹配项时提示用户：可直接填自由文本
                            <div className="px-3 py-2 text-xs text-slate-400">
                              无匹配项，可直接输入自由文本（如"3 岁"、"成人高考"等）
                            </div>
                          )}
                          <div className="px-3 py-1.5 text-[10px] text-slate-400 border-t border-slate-100 dark:border-slate-700">
                            💡 也可直接输入自由文本（如"3 岁"、"成人高考"等）
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <label className="text-[11px] text-slate-500">科目 *</label>
                  {/* 老板需求：科目改填写（不选择）— 灵活支持任何科目 */}
                  <input
                    type="text"
                    placeholder="如：数学 / 编程 / 英语..."
                    className="mt-1 w-full h-9 px-2 text-xs border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={50}
                  />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500">教材版本</label>
                  {/* 老板需求：教材版本改填写（不选择）— 灵活支持任何版本 */}
                  <input
                    type="text"
                    placeholder="如：人教版 / 北师大版..."
                    className="mt-1 w-full h-9 px-2 text-xs border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800"
                    value={textbookVersion}
                    onChange={(e) => setTextbookVersion(e.target.value)}
                    maxLength={50}
                  />
                </div>
              </div>
            </div>

            {/* "开始演练"主按钮 */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleStartClick}
                disabled={!canStart && checked}
                className={cn(
                  'flex-1 h-11 rounded-xl flex items-center justify-center gap-2 transition-all text-sm font-semibold',
                  canStart
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:opacity-90 shadow-md hover:shadow-lg cursor-pointer'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed',
                )}
              >
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    <span>准备中...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="size-4" />
                    <span>开始演练</span>
                    <span>→</span>
                  </>
                )}
              </button>
              <button
                onClick={() => router.push('/')}
                className="h-11 px-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-sm font-medium flex items-center gap-1"
              >
                <ArrowLeft className="size-3.5" />
                <span>返回课堂</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* ── 底部链接区 ──
            2026-08-02 老板 P0 批改：
            1) PC 端：底部 4 按钮直接展示（我的习题/我的错题/我的收藏/我的答卷）
               - 跟原 "我的题库" 单链位置一样，hover 紫渐变
               - icon 用 lucide (fa-pencil / fa-times-circle / fa-star / fa-file-text-o)
            2) Mobile 端：底部去掉单链，换成 fixed bottom-right FAB 悬浮按钮
               - 点击展开 4 项 bottom sheet（紫渐变 + 大圆 + shadow）
               - 4 项内容跟 PC 一致，但放进 popover 点击跳转
            === 移动端 FAB + bottom sheet 设计思路 ===
            - FAB 位置：fixed bottom-20 right-4（避开 .mobile-tabbar 65px）
            - FAB 形状：圆形 56x56，紫渐变 + 白 Library icon
            - Sheet 内容：4 个 list item，每个 item icon + 标题 + 短描述，点击跳 /index/user/quiz?tab=X
            - 关闭逻辑：点 FAB 切换 / 点 sheet 外黑色遮罩 / X 按钮都能关
            === Trade-off ===
            - Mobile 端用户需要多一次点击（FAB → sheet → tab），但符合大厂设计（避免一开始就 8 个链接堆底部）

            2026-08-02 v6.97.58 P0 老板反馈"PC 端 4 按钮要做显眼, 像大厂产品":
            - 旧版: `inline-flex gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 hover:border-violet-300 hover:bg-violet-50`
              → 偏文静 (Apple 风冷色调), 跟"开始演练"绿渐变 CTA 视觉权重不平衡
            - 新版: 卡片式入口 (大厂 standard pattern = 彩色 icon box + 标题 + 副标题)
              → size-10 圆角渐变 icon box (hover 反白成实色填充)
              → 标题 text-base font-bold + 副标题 text-[11px]
              → hover: -translate-y-0.5 + shadow-lg + border 颜色饱和
              → 每个 tab 独立彩色主题 (紫/玫红/琥珀/蓝) → 视觉层次分明
        */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="relative z-20 mt-8 hidden sm:flex items-stretch justify-center gap-3 flex-wrap px-4"
        >
          {/* PC 端 4 卡片式入口（按 tab 顺序） */}
          <a href={typeof window !== 'undefined' ? window.location.origin + '/index/user/quiz?tab=questions' : '/index/user/quiz?tab=questions'} className="group inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border-2 border-slate-200 hover:border-violet-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 min-h-[56px]">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 text-violet-600 group-hover:bg-gradient-to-br group-hover:from-violet-500 group-hover:to-purple-600 group-hover:text-white transition-all">
              <i className="fa fa-pencil text-lg" aria-hidden="true" />
            </span>
            <div className="flex flex-col items-start leading-tight">
              <span className="text-sm font-bold text-slate-800 group-hover:text-violet-700 transition-colors">我的习题</span>
              <span className="text-[11px] text-slate-500 mt-0.5">所有做过的练习题</span>
            </div>
          </a>
          <a href={typeof window !== 'undefined' ? window.location.origin + '/index/user/quiz?tab=wrong' : '/index/user/quiz?tab=wrong'} className="group inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border-2 border-slate-200 hover:border-rose-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 min-h-[56px]">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-rose-100 to-pink-100 text-rose-600 group-hover:bg-gradient-to-br group-hover:from-rose-500 group-hover:to-pink-600 group-hover:text-white transition-all">
              <i className="fa fa-times-circle text-lg" aria-hidden="true" />
            </span>
            <div className="flex flex-col items-start leading-tight">
              <span className="text-sm font-bold text-slate-800 group-hover:text-rose-700 transition-colors">我的错题</span>
              <span className="text-[11px] text-slate-500 mt-0.5">答错的题一键强化</span>
            </div>
          </a>
          <a href={typeof window !== 'undefined' ? window.location.origin + '/index/user/quiz?tab=favorites' : '/index/user/quiz?tab=favorites'} className="group inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border-2 border-slate-200 hover:border-amber-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 min-h-[56px]">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 text-amber-600 group-hover:bg-gradient-to-br group-hover:from-amber-500 group-hover:to-orange-600 group-hover:text-white transition-all">
              <i className="fa fa-star text-lg" aria-hidden="true" />
            </span>
            <div className="flex flex-col items-start leading-tight">
              <span className="text-sm font-bold text-slate-800 group-hover:text-amber-700 transition-colors">我的收藏</span>
              <span className="text-[11px] text-slate-500 mt-0.5">收藏的好题与易错点</span>
            </div>
          </a>
          <a href={typeof window !== 'undefined' ? window.location.origin + '/index/user/quiz?tab=exams' : '/index/user/quiz?tab=exams'} className="group inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border-2 border-slate-200 hover:border-blue-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 min-h-[56px]">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-100 to-sky-100 text-blue-600 group-hover:bg-gradient-to-br group-hover:from-blue-500 group-hover:to-sky-600 group-hover:text-white transition-all">
              <i className="fa fa-file-text-o text-lg" aria-hidden="true" />
            </span>
            <div className="flex flex-col items-start leading-tight">
              <span className="text-sm font-bold text-slate-800 group-hover:text-blue-700 transition-colors">我的答卷</span>
              <span className="text-[11px] text-slate-500 mt-0.5">查看历次完整答卷</span>
            </div>
          </a>
        </motion.div>
        {/* PC 端底部说明文字（账单提示） */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.65 }}
          className="relative z-20 mt-4 hidden sm:block text-center text-[11px] text-slate-400 px-4"
        >
          每次演练扣 {exerciseMinutes} 分钟课时 · 账单备注"习题演练"
        </motion.div>
      </div>

      {/* ═══ 2026-08-02 老板 P0 — 移动端「我的题库」悬浮按钮 (FAB) + Bottom Sheet ═══
          === 设计目标 ===
          1. FAB 位置：fixed bottom-20 right-4（避开 .mobile-tabbar 65px + safe area）
             - z-[9999]（高于 mobile-tabbar z-9998，确保不被遮挡）
          2. FAB 形状：56x56 圆形 + bg-gradient-to-br from-violet-500 to-purple-600 + shadow-lg
             - 默认显示 Library icon（白色）+ 顶部小脉冲红点
             - hover/active scale 0.95 微交互
          3. Bottom Sheet 内容：
             - 顶部大圆柄 (handle) 提示可拖拽关
             - 标题："我的题库" + 4 项描述快捷说明
             - 4 项 list item，每项：icon box（彩色）+ 标题 + 短描述 + 箭头
             - 点击立即跳转 /index/user/quiz?tab=X
          4. 关闭逻辑：
             - 点 FAB 切换
             - 点 sheet 外黑色遮罩（bg-black/50）
             - 按 ESC 关闭
          5. 仅 mobile 端显示（sm:hidden）
          === 跟原版的"我的题库"单链区别 ===
          - 原版 line 543-559：底部一行 inline `<a>` 标签
          - 新版 mobile：底部 hidden + FAB fixed 替代 → 更符合大厂（不堆 4 链接到表单下方） */}
      {quizSheetOpen && (
        <div
          className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm sm:hidden"
          onClick={() => setQuizSheetOpen(false)}
          aria-hidden="true"
        />
      )}
      <button
        type="button"
        aria-label={quizSheetOpen ? '关闭我的题库' : '打开我的题库'}
        onClick={() => setQuizSheetOpen((o) => !o)}
        className={cn(
          'fixed bottom-20 right-4 z-[9999] sm:hidden',
          'size-14 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/30 active:scale-95 transition-transform',
          'flex items-center justify-center',
        )}
      >
        <Library className="size-6" strokeWidth={2.5} />
        {/* 顶部小脉冲红点，提示"有新内容"（永远显示，建立肌肉记忆） */}
        <span className="absolute top-1 right-1 size-2.5 rounded-full bg-rose-500 ring-2 ring-white" />
      </button>

      {/* Bottom Sheet (仅在 quizSheetOpen=true 时渲染) */}
      {quizSheetOpen && (
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-[9999] sm:hidden bg-white rounded-t-3xl shadow-2xl shadow-slate-900/30 max-h-[75vh] overflow-hidden flex flex-col"
          role="dialog"
          aria-label="我的题库"
        >
          {/* 大圆柄（让用户感觉可下拉关闭） */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="size-1.5 w-12 rounded-full bg-slate-300" />
          </div>

          {/* Header */}
          <div className="px-6 pt-2 pb-4 text-center border-b border-slate-100">
            <div className="mx-auto mb-2 flex size-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500/15 to-purple-500/15 ring-1 ring-violet-200/50">
              <Library className="size-7 text-violet-600" strokeWidth={2.25} />
            </div>
            <h3 className="text-lg font-semibold text-slate-800">我的题库</h3>
            <p className="text-xs text-slate-500 mt-1">查看你做过的题、错题、收藏与答卷</p>
          </div>

          {/* 4 项 Tab List */}
          <nav className="flex-1 overflow-y-auto py-2 px-3">
            <ul className="space-y-1">
              <li>
                <a
                  href={typeof window !== 'undefined' ? window.location.origin + '/index/user/quiz?tab=questions' : '/index/user/quiz?tab=questions'}
                  className="flex items-center gap-4 px-4 py-3.5 rounded-2xl hover:bg-violet-50 active:bg-violet-100 transition-colors group"
                  onClick={() => setQuizSheetOpen(false)}
                >
                  <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 text-violet-600">
                    <i className="fa fa-pencil text-lg" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="font-medium text-slate-800 group-hover:text-violet-700">我的习题</div>
                    <div className="text-xs text-slate-500 mt-0.5">所有做过的练习题</div>
                  </div>
                  <i className="fa fa-chevron-right text-slate-300 group-hover:text-violet-400" aria-hidden="true" />
                </a>
              </li>
              <li>
                <a
                  href={typeof window !== 'undefined' ? window.location.origin + '/index/user/quiz?tab=wrong' : '/index/user/quiz?tab=wrong'}
                  className="flex items-center gap-4 px-4 py-3.5 rounded-2xl hover:bg-rose-50 active:bg-rose-100 transition-colors group"
                  onClick={() => setQuizSheetOpen(false)}
                >
                  <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-rose-100 to-pink-100 text-rose-600">
                    <i className="fa fa-times-circle text-lg" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="font-medium text-slate-800 group-hover:text-rose-700">我的错题</div>
                    <div className="text-xs text-slate-500 mt-0.5">答错的题一键强化</div>
                  </div>
                  <i className="fa fa-chevron-right text-slate-300 group-hover:text-rose-400" aria-hidden="true" />
                </a>
              </li>
              <li>
                <a
                  href={typeof window !== 'undefined' ? window.location.origin + '/index/user/quiz?tab=favorites' : '/index/user/quiz?tab=favorites'}
                  className="flex items-center gap-4 px-4 py-3.5 rounded-2xl hover:bg-amber-50 active:bg-amber-100 transition-colors group"
                  onClick={() => setQuizSheetOpen(false)}
                >
                  <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 text-amber-600">
                    <i className="fa fa-star text-lg" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="font-medium text-slate-800 group-hover:text-amber-700">我的收藏</div>
                    <div className="text-xs text-slate-500 mt-0.5">收藏的好题与易错点</div>
                  </div>
                  <i className="fa fa-chevron-right text-slate-300 group-hover:text-amber-400" aria-hidden="true" />
                </a>
              </li>
              <li>
                <a
                  href={typeof window !== 'undefined' ? window.location.origin + '/index/user/quiz?tab=exams' : '/index/user/quiz?tab=exams'}
                  className="flex items-center gap-4 px-4 py-3.5 rounded-2xl hover:bg-blue-50 active:bg-blue-100 transition-colors group"
                  onClick={() => setQuizSheetOpen(false)}
                >
                  <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-100 to-sky-100 text-blue-600">
                    <i className="fa fa-file-text-o text-lg" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="font-medium text-slate-800 group-hover:text-blue-700">我的答卷</div>
                    <div className="text-xs text-slate-500 mt-0.5">查看历次完整答卷</div>
                  </div>
                  <i className="fa fa-chevron-right text-slate-300 group-hover:text-blue-400" aria-hidden="true" />
                </a>
              </li>
            </ul>
          </nav>

          {/* Footer 安全留白 + 关闭按钮（额外一行） */}
          <div className="border-t border-slate-100 p-3" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
            <button
              type="button"
              onClick={() => setQuizSheetOpen(false)}
              className="w-full py-3 rounded-2xl bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-700 text-sm font-medium transition-colors"
            >
              关闭
            </button>
          </div>
        </motion.div>
      )}

      {/* ── 二次确认弹窗 ── */}
      <AlertDialog open={confirmOpen} onOpenChange={(o) => !submitting && setConfirmOpen(o)}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>确认开启习题演练？</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-2 text-sm">
                <p className="font-medium text-amber-700 dark:text-amber-300">
                  ⚠️ 每次习题演练扣 {exerciseMinutes} 分钟课时，不再额外扣费。
                </p>
                <p>
                  AI 将根据你填写的配置生成专属试卷，提交后由 AI 批改。
                </p>
                <div className="mt-3 p-2.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800 text-xs space-y-1">
                  <div className="flex gap-1.5"><span className="text-slate-500">演练类型：</span><span className="font-medium text-emerald-700 dark:text-emerald-300">{exerciseType}</span></div>
                  <div className="flex gap-1.5"><span className="text-slate-500">科目：</span><span className="font-medium text-emerald-700 dark:text-emerald-300">{subject}</span></div>
                  {educationLevel && <div className="flex gap-1.5"><span className="text-slate-500">阶段：</span><span className="font-medium text-emerald-700 dark:text-emerald-300">{educationLevel}{educationGrade ? ` · ${educationGrade}` : ''}</span></div>}
                  {textbookVersion && <div className="flex gap-1.5"><span className="text-slate-500">教材版本：</span><span className="font-medium text-emerald-700 dark:text-emerald-300">{textbookVersion}</span></div>}
                </div>
                <p className="text-xs text-muted-foreground">
                  账单备注会显示"习题演练"，方便你区分日常课堂和习题演练的消耗。
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmStart} disabled={submitting}>
              {submitting ? <Loader2 className="size-4 animate-spin" /> : '确认开启'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}